import { API_BASE, normalizeApiBase } from '../config/api';

export const DEFAULT_ORG_PROVIDER = 'openrouter';
export const DEFAULT_ORG_MODEL = 'openai/gpt-oss-120b';

export type ModelCatalogEntry = {
  provider: string;
  model: string;
  created_at?: string;
};

export type ProviderInfo = {
  id: string;
  configured: boolean;
  base_url?: string;
};

export type OrgSettingsRow = {
  org_name: string;
  provider: string;
  model: string;
  updated_at?: string;
};

const SELECTED_ORG_KEY = 'admin_llm_selected_org';

export function resolveLlmApiBase(): string {
  const stored = (localStorage.getItem('admin_api_base') || '').trim();
  if (stored) return normalizeApiBase(stored);
  if (API_BASE) return API_BASE;
  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/+$/, '');
  }
  return '';
}

export function getSelectedOrg(): string {
  return (localStorage.getItem(SELECTED_ORG_KEY) || '').trim();
}

export function setSelectedOrg(org: string) {
  const trimmed = org.trim();
  if (trimmed) localStorage.setItem(SELECTED_ORG_KEY, trimmed);
  else localStorage.removeItem(SELECTED_ORG_KEY);
}

function llmUrl(path: string): string {
  const base = resolveLlmApiBase();
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export async function fetchProviders(): Promise<ProviderInfo[]> {
  const res = await fetch(llmUrl('/api/v2/llm/providers'));
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  const data = (await res.json()) as { providers?: ProviderInfo[] };
  return Array.isArray(data.providers) ? data.providers : [];
}

export async function fetchModelCatalog(): Promise<ModelCatalogEntry[]> {
  const res = await fetch(llmUrl('/api/v2/llm/models/catalog'));
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  const data = (await res.json()) as { models?: ModelCatalogEntry[] };
  return Array.isArray(data.models) ? data.models : [];
}

export async function addModelCatalog(provider: string, model: string): Promise<ModelCatalogEntry> {
  const res = await fetch(llmUrl('/api/v2/llm/models/catalog'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, model }),
  });
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  return (await res.json()) as ModelCatalogEntry;
}

export async function deleteModelCatalog(provider: string, model: string): Promise<void> {
  const res = await fetch(llmUrl('/api/v2/llm/models/catalog'), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider, model }),
  });
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
}

export async function fetchOrgSettingsList(): Promise<OrgSettingsRow[]> {
  const res = await fetch(llmUrl('/api/v2/llm/settings'));
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  const data = (await res.json()) as { settings?: OrgSettingsRow[] };
  const rows = Array.isArray(data.settings) ? data.settings : [];
  return rows.map(normalizeOrgRow);
}

export async function fetchOrgSettings(org: string): Promise<OrgSettingsRow | null> {
  const res = await fetch(llmUrl(`/api/v2/llm/settings/${encodeURIComponent(org)}`));
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  return normalizeOrgRow((await res.json()) as OrgSettingsRow);
}

export async function saveOrgSettings(
  org: string,
  body: { provider: string; model: string },
): Promise<OrgSettingsRow> {
  const res = await fetch(llmUrl(`/api/v2/llm/settings/${encodeURIComponent(org)}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
  return normalizeOrgRow((await res.json()) as OrgSettingsRow);
}

export async function deleteOrgSettings(org: string): Promise<void> {
  const res = await fetch(llmUrl(`/api/v2/llm/settings/${encodeURIComponent(org)}`), {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
}

export async function reloadLlmGateway(): Promise<void> {
  const res = await fetch(llmUrl('/api/v2/llm/reload'), { method: 'POST' });
  if (!res.ok) throw new Error(parseApiError(await res.text(), res.status));
}

function normalizeOrgRow(row: OrgSettingsRow): OrgSettingsRow {
  return {
    ...row,
    provider: row.provider || DEFAULT_ORG_PROVIDER,
    model: row.model || DEFAULT_ORG_MODEL,
  };
}

export function defaultOrgAssignment(): { provider: string; model: string } {
  return { provider: DEFAULT_ORG_PROVIDER, model: DEFAULT_ORG_MODEL };
}

function parseApiError(text: string, status: number): string {
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* not json */
  }
  if (text.trimStart().startsWith('<')) {
    return `HTTP ${status} — received HTML. Check LLM gateway URL or nginx proxy on port 81.`;
  }
  return text || `HTTP ${status}`;
}

export function isDatabaseUnavailableError(message: string): boolean {
  return /database not available/i.test(message);
}

export function modelsForProvider(catalog: ModelCatalogEntry[], provider: string): ModelCatalogEntry[] {
  return catalog.filter((e) => e.provider === provider);
}
